import { useEffect, useMemo, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { useAllocatedDedicatedClis, useUserLocations, useUserModules } from '../../api';
import type { VoiceCampaignFormValues } from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}

export function StepRouting({ values, setValues }: Props) {
  const locations = useUserLocations();
  const dedicatedClis = useAllocatedDedicatedClis();
  const modules = useUserModules();

  const hasDedicatedCli = useMemo(
    () =>
      (modules.data ?? []).some((m) => m.moduleName === 'Dedicated Cli'),
    [modules.data],
  );

  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [locations.data],
  );

  /*
   * Auto-select every location the first time the list loads. The ref guard
   * means we only do this once — if the user deselects locations manually
   * we won't re-fill on subsequent renders. Also skip if the form is being
   * hydrated for clone / update flows (locationIds already has data).
   */
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (locationOptions.length === 0) return;
    if (values.locationIds.length > 0) {
      // Hydrated form (clone/update) already has selections — skip auto-fill.
      autoSelectedRef.current = true;
      return;
    }
    autoSelectedRef.current = true;
    setValues({ locationIds: locationOptions.map((o) => o.value) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions]);

  const cliOptions = useMemo(
    () =>
      (dedicatedClis.data ?? []).map((c) => ({
        value: String(c.cliId),
        label: c.cli,
      })),
    [dedicatedClis.data],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1">
            Locations
            <span className="text-destructive">*</span>
          </CardTitle>
          <CardDescription>
            The campaign will dial out from these locations. Pick at least one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>
            Selected locations
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <MultiSelect
            options={locationOptions}
            value={values.locationIds}
            onChange={(v) => setValues({ locationIds: v })}
            placeholder={
              locations.isLoading
                ? 'Loading…'
                : locationOptions.length === 0
                  ? 'No locations available'
                  : 'Pick locations'
            }
            searchPlaceholder="Filter locations…"
          />
        </CardContent>
      </Card>

      {hasDedicatedCli && (
        <Card>
          <CardHeader>
            <CardTitle>Dedicated CLIs</CardTitle>
            <CardDescription>
              Pick which dedicated CLIs to dial from. Leave empty to use the shared pool.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label>Selected CLIs</Label>
            <MultiSelect
              options={cliOptions}
              value={values.selectedCLI}
              onChange={(v) => setValues({ selectedCLI: v })}
              placeholder={
                dedicatedClis.isLoading
                  ? 'Loading…'
                  : cliOptions.length === 0
                    ? 'No dedicated CLIs allocated'
                    : 'Pick CLIs'
              }
              searchPlaceholder="Filter CLIs…"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
